import React, { useEffect, useMemo, useState } from 'react';
import { Icon, IconName } from 'react-frontier';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

interface CalendarProps{
	startDate: Date,
	endDate: Date,
	loading?: boolean,
	emptyLabel?: string,
	dayLabels?: {
		day: number,
		month: number,
		year: number,
		label: string,
		className?: string,
		iconName?: IconName,
	}[],
	onViewingMonthChange?: (month: number, year: number)=>void,
	onDateChange: (start_date: Date, end_date: Date)=>void,
}

enum SelectMode{
	DAY = 1,
	WEEK = 2,
	MONTH = 3,
}

const WEEKDAYS = [
	'Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'
]
const MONTHS = [
	'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
]

interface MonthDay{
	day: number,
	month: number, 
	year: number,
	weekday: number,
}

var toMonthDay = (date: Date) : MonthDay=>{
	return {
		day: date.getDate(),
		month: date.getMonth()+1,
		year: date.getFullYear(),
		weekday: date.getDay(),
	}
}

var fromMonthDay = (date: MonthDay)=>{
	return new Date(date.year, date.month-1, date.day, 12);
}

var moveMonthDay = (md: MonthDay, amt: number) : MonthDay=>{
	var dt = new Date(md.year, md.month-1, md.day, 12);
	dt.setDate(dt.getDate()+amt);
	return toMonthDay(dt);
}

var betweenMonthDay = (date: MonthDay, start: MonthDay, end: MonthDay)=>{
	return (date.year>=start.year && (date.month>start.month || (date.month==start.month && date.day>=start.day))) && (date.year<=end.year && (date.month<end.month || (date.month==end.month && date.day<=end.day)));
}

var isMonthDay = (a: MonthDay, b: MonthDay)=>{
	return a.day===b.day && a.month===b.month && a.year===b.year;
}

var Calendar = (props: CalendarProps)=>{
	var { t } = useTranslation();
	var [mode, setMode] = useState<SelectMode>(SelectMode.DAY);
	var [shownMonth, setShownMonth] = useState<MonthDay>(null);

	var today = useMemo(()=>{
		return toMonthDay(new Date());
	}, []);

	var current_range = useMemo(()=>{
		var start : MonthDay = null, end : MonthDay = null;
		if(!props.startDate && !props.endDate){
			start = toMonthDay(new Date());
			end = toMonthDay(new Date());
		}else{
			start = toMonthDay(props.startDate || props.endDate);
			end = toMonthDay(props.endDate || props.startDate);
		}
		return { start, end };
	}, [props.startDate, props.endDate]);

	var current_month = useMemo(()=>{
		var month = shownMonth ? shownMonth.month : current_range.start.month;
		var year = shownMonth ? (shownMonth.year) : current_range.start.year;
		var start_date = toMonthDay(new Date(year, month-1, 1, 12));
		var end_date = toMonthDay(new Date(year, month, 0, 12));
		
		var days : MonthDay[][] = [], current_week : MonthDay[] = [];
		for(let i=start_date.weekday-1; i>=0; i--){
			var prev_day = moveMonthDay(start_date, -i-1);
			current_week.push(prev_day);
		}

		while(start_date.day<=end_date.day && start_date.month<=end_date.month && start_date.year<=end_date.year){
			current_week.push(start_date);
			start_date = moveMonthDay(start_date, 1);
			
			if(current_week.length>=7){
				days.push(current_week);
				current_week = [];
			}
		}

		if(current_week.length>0){
			if(current_week.length<7){
				var next_month = end_date.month>=12 ? 0 : end_date.month+1;
				var next_year = end_date.month>=12 ? end_date.year+1 : end_date.year;

				var i = 0;
				while(current_week.length<7){
					current_week.push({
						day: i+1,
						month: next_month,
						year: next_year,
						weekday: end_date.weekday+(i+1)
					});
					i++;
				}
			}
			days.push(current_week);
		}
		return {
			month,
			year,
			days
		};
	}, [shownMonth, current_range]);

	useEffect(()=>{
		if(props.onViewingMonthChange) props.onViewingMonthChange(current_month.month, current_month.year);
	}, [current_month.month, current_month.year]);

	var changeMonth = (amt: number)=>{
		return ()=>{
			var current_month : MonthDay = null;
			if(shownMonth) current_month = {...shownMonth};
			else current_month = {...current_range.start};

			current_month.month += amt;
			if(current_month.month>12){
				current_month.month = 1;
				current_month.year++;
			}else if(current_month.month<1){
				current_month.month = 12;
				current_month.year--;
			}
			setShownMonth(current_month);
		}
	}

	var dateSelected = (date: MonthDay, mode_override?: SelectMode)=>{
		return ()=>{
			if(mode_override) mode = mode_override
			var d = fromMonthDay(date);
			if(mode===SelectMode.DAY){
				return props.onDateChange(d, d);
			}else if(mode===SelectMode.WEEK){
				var start = moveMonthDay(date, -date.weekday);
				var end = moveMonthDay(start, 6);
				return props.onDateChange(fromMonthDay(start), fromMonthDay(end));
			}
		}
	}

	var changeMode = (mode: SelectMode)=>{
		return ()=>{
			setMode(mode);
			dateSelected(current_range.start, mode)();
		}
	}
	
	return <div className="ar calendar">
		<div className="header">
			<div className="date">
				<div className="day">{MONTHS[current_range.start.month-1].substring(0, 3)}</div>
				<div className="number">{current_range.start.day}</div>
			</div>
			<div className="month">
				{MONTHS[current_month.month-1]} {current_month.year}
			</div>
			<div className="control">
				{!!props.loading && (
					<div className="fr inline loading"></div>
				)}
				<div className="left" onClick={changeMonth(-1)}>
					<Icon name='arrow-left' />
				</div>
				<div className="right" onClick={changeMonth(1)}>
					<Icon name='arrow-right' />
				</div>
			</div>
		</div>
		<div className="quick">
			<div className={classNames("item", { active: mode===SelectMode.DAY })} onClick={changeMode(SelectMode.DAY)}>Día</div>
			<div className={classNames("item", { active: mode===SelectMode.WEEK })} onClick={changeMode(SelectMode.WEEK)}>Semana</div>
			{}
		</div>
		<table cellSpacing={0}>
			<thead>
				<tr>
					{WEEKDAYS.map((a, i)=><th key={`WKDY-${i}`}>{a}</th>)}
				</tr>
			</thead>
			<tbody>
				{current_month.days.map((w, i)=>(
					<tr key={`MTHWK-${current_month.month}-${i}`}>
						{w.map(a=>{
							var day_label = props.dayLabels && props.dayLabels.find(d=>d.day===a.day && d.month===a.month && d.year===a.year);
							
							return <td key={`MTHDY-${a.day}-${a.month}-${a.year}`}>
								<div onClick={dateSelected(a)} className={classNames('day', { 
									empty: !a,
									today: isMonthDay(today, a),
									next: a.month!=current_month.month,
									selected: betweenMonthDay(a, current_range.start, current_range.end),
									start: isMonthDay(a, current_range.start),
									end: isMonthDay(a, current_range.end)
								})}>
									<div className="text">
										{a ? a.day : null}
										{day_label ? (
											<div className={classNames('label', day_label.className)}>
												{!!day_label.iconName && <Icon name={day_label.iconName} />}
												{day_label.label}
											</div>
										) : props.emptyLabel ? (
											<div className="label empty">{props.emptyLabel}</div>
										) : null}
									</div>
									{}
								</div>
							</td>
						})}
					</tr>
				))}
			</tbody>
		</table>
	</div>
}

export default Calendar;